import React, { useCallback, useEffect, useState, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { FiPlus, FiCheck } from "react-icons/fi";
import { MdOutlineInsertChartOutlined } from "react-icons/md";
import GenderChat from "./Charts/GenderChat";
import Agechart from "./Charts/Agechart";
import DeviceChart from "./Charts/DeviceChart";
import AuctionInsightsUploadChart from "./Charts/AuctionInsights";
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import DonutChartGroups from "./Charts/DonutChartGroups";
import { apiRequestBodyBuilder } from "./config/tableHelper";

const AddChart = (props) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [groupNames, setGroupNames] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [chartArray, setChartArray] = useState([]);
  const [loading, setLoading] = useState(true);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest?.(".MuiPopover-root") &&
        !event.target.closest?.(".MuiMenu-root")
      ) {
        handleCancel();
      }
    };

    if (showOptions || selectedOption) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions, selectedOption]);

  const handleAddTable = () => setShowOptions((prev) => !prev);

  const handleCancel = () => {
    setShowOptions(false);
    setSelectedOption(null);
    setSelectedGroups([]);
  };

  const fetchGroupNames = useCallback(async () => {
    try {
      let initialDateRange = [
        {
          startDate: new Date(2022, 1, 1),
          endDate: new Date(),
          key: "selection",
        },
      ];
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!responseInfo.ok) throw new Error("Failed to fetch group info");
      const groupType = await responseInfo.json();
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      let resData = await response.json();
      setGroupNames(
        resData.groups.map((g) => ({
          group_id: g.group_id,
          group_name: g.group_name,
        })),
      );
    } catch (err) {
      console.error("Failed to fetch groups");
    }
  }, []);

  const handleGroupChange = (event) => setSelectedGroups(event.target.value);

  useEffect(() => {
    fetchGroupNames();
    fetchVisibleCharts();
  }, []);

  const addTable = () => {
    if (selectedOption === "chart" && selectedGroups.length === 0) {
      alert("Please select at least one group.");
      return;
    }
    const chartTypes = {
      chart: "chart",
      age: "age",
      gender: "gender",
      device: "device",
      auction_insights: "auction_insights",
    };
    let tableName = chartTypes[selectedOption];
    let currentCount =
      chartArray.find((item) => item.table_name === tableName)?.count ?? 0;
    toggleVisibleCharts(
      true,
      tableName,
      currentCount,
      "impressions",
      selectedGroups,
    );
    setSelectedGroups([]);
    setSelectedOption(null);
    setShowOptions(false);
  };

  const removeArrayIndex = (id) => {
    if (window.confirm("Delete this chart?"))
      toggleVisibleCharts(
        false,
        chartArray[id].table_name,
        chartArray[id].count,
        chartArray[id].metric,
        chartArray[id].group_ids,
      );
  };

  const fetchVisibleCharts = async () => {
    setLoading(true);
    await axios
      .post("https://api.confidanto.com/table-visibility-chart/get", {
        project_id: localStorage.getItem("project_id"),
        customer_id: localStorage.getItem("customer_id"),
        email: localStorage.getItem("email"),
        page_type: props.PageType,
      })
      .then((res) => setChartArray(res.data))
      .catch((err) => setChartArray([]))
      .finally(() => setLoading(false));
  };

  const toggleVisibleCharts = (
    isAdd,
    tableName,
    currentCount,
    metric,
    GroupIds = [],
  ) => {
    let requestBody = {
      table_name: tableName,
      metric: metric,
      group_ids: GroupIds,
      count: currentCount,
      project_id: localStorage.getItem("project_id"),
      customer_id: localStorage.getItem("customer_id"),
      email: localStorage.getItem("email"),
      page_type: props.PageType,
    };
    if (isAdd) requestBody.count += 1;
    else {
      if (currentCount >= 2) requestBody.count -= 1;
      else requestBody.action = "delete";
    }
    axios
      .post("https://api.confidanto.com/table-visibility-chart", requestBody)
      .finally(() => fetchVisibleCharts());
  };

  const updateTableMetric = (id, oldMetric, newMetric) => {
    axios.post("https://api.confidanto.com/table-visibility-chart", {
      project_id: chartArray[id].project_id,
      table_name: chartArray[id].table_name,
      metric: newMetric,
      group_ids: chartArray[id].group_ids,
      count: chartArray[id].count,
    });
  };

  const isChartTypeAdded = (type) =>
    chartArray.some(
      (item) =>
        (item.table_name === type || item.type === type) && item.count > 0,
    );

  const pageTypeMap = {
    daily_reporting: "daily",
    weekly_reporting: "weekly",
    monthly_reporting: "monthly",
    project_details: null,
  };

  const totalChartCount = chartArray.reduce(
    (acc, item) => acc + (item.count || 0),
    0,
  );

  return (
    <div className="w-full flex flex-col items-start space-y-4">
      {/* Top Header Control Bar */}
      <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 print:hidden">
        <div className="flex items-center gap-2">
          <MdOutlineInsertChartOutlined className="text-indigo-600 text-xl" />
          <h3 className="text-base font-semibold text-slate-800">
            Visual Charts
          </h3>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
            {totalChartCount}
          </span>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          {!showOptions ? (
            <button
              onClick={handleAddTable}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 shadow-2xs transition"
            >
              <FiPlus className="text-sm" />
              <span>Add Chart</span>
            </button>
          ) : (
            <div
              ref={dropdownRef}
              className="flex items-center flex-wrap gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm animate-fade-in"
            >
              <FormControl size="small" className="w-44">
                <InputLabel className="text-xs">Select Chart Type</InputLabel>
                <Select
                  value={selectedOption || ""}
                  label="Select Chart Type"
                  onChange={(e) => setSelectedOption(e.target.value)}
                  sx={{
                    fontSize: "0.85rem",
                    height: 36,
                    borderRadius: "0.5rem",
                  }}
                >
                  <MenuItem value="chart" disabled={isChartTypeAdded("chart")}>
                    Group
                  </MenuItem>
                  <MenuItem value="age" disabled={isChartTypeAdded("age")}>
                    Audience
                  </MenuItem>
                  <MenuItem
                    value="gender"
                    disabled={isChartTypeAdded("gender")}
                  >
                    Gender
                  </MenuItem>
                  <MenuItem
                    value="device"
                    disabled={isChartTypeAdded("device")}
                  >
                    Device
                  </MenuItem>
                  <MenuItem
                    value="auction_insights"
                    disabled={isChartTypeAdded("auction_insights")}
                  >
                    Auction Insights Upload
                  </MenuItem>
                </Select>
              </FormControl>

              {selectedOption === "chart" && (
                <FormControl size="small" className="w-48">
                  <InputLabel className="text-xs">Select Groups</InputLabel>
                  <Select
                    multiple
                    value={selectedGroups}
                    label="Select Groups"
                    onChange={handleGroupChange}
                    renderValue={(selected) => selected.join(", ")}
                    sx={{
                      fontSize: "0.85rem",
                      height: 36,
                      borderRadius: "0.5rem",
                    }}
                  >
                    {groupNames.map((group) => (
                      <MenuItem key={group.group_id} value={group.group_name}>
                        <Checkbox
                          size="small"
                          checked={selectedGroups.includes(group.group_name)}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: "0.85rem",
                          }}
                          primary={group.group_name}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedOption && (
                <button
                  onClick={addTable}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-4 h-9 rounded-lg transition shadow-xs flex items-center gap-1.5"
                >
                  <FiCheck className="text-sm" />
                  Create
                </button>
              )}

              <button
                type="button"
                onClick={handleCancel}
                title="Close"
                className="w-9 h-9 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg transition shadow-xs"
                aria-label="Close select"
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chart List */}
      <div className="w-full chart-list print:block">
        {loading ? (
          <div className="py-8 flex items-center gap-2 text-slate-500">
            <CircularProgress size={20} className="text-indigo-600" />
            <span className="text-sm font-medium">Loading saved charts...</span>
          </div>
        ) : totalChartCount === 0 ? (
          <div className="w-full py-8 border-2 border-dashed border-slate-200/90 rounded-2xl flex flex-col items-center justify-center text-center bg-white/40 print:hidden">
            <MdOutlineInsertChartOutlined className="text-slate-300 text-3xl mb-2" />
            <p className="text-xs font-semibold text-slate-600">
              No charts added yet
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Click "+ Add Chart" above to view metrics in donut and bar charts.
            </p>
          </div>
        ) : (
          chartArray.map((item, groupIndex) =>
            Array.from({ length: item.count }).map((_, innerIndex) => {
              const uniqueKey = `${groupIndex}-${innerIndex}`;
              switch (item.table_name) {
                case "chart":
                  return (
                    <div
                      key={uniqueKey}
                      className="print:block print:break-inside-avoid print:page-break-inside-avoid w-full print:mt-4 mb-6"
                    >
                      <DonutChartGroups
                        groupNames={item.group_ids}
                        metric={item.metric}
                        arrayIndex={groupIndex}
                        removeArrayIndex={removeArrayIndex}
                        previousDates={props.previousDates}
                        updateTableMetric={updateTableMetric}
                      />
                    </div>
                  );
                case "age":
                  return (
                    <AudienceElement
                      key={uniqueKey}
                      metric={item.metric}
                      arrayIndex={groupIndex}
                      removeArrayIndex={removeArrayIndex}
                      previousDates={props.previousDates}
                      updateTableMetric={updateTableMetric}
                      PageType={pageTypeMap[props.PageType]}
                    />
                  );
                case "gender":
                  return (
                    <GenderElement
                      key={uniqueKey}
                      metric={item.metric}
                      arrayIndex={groupIndex}
                      removeArrayIndex={removeArrayIndex}
                      previousDates={props.previousDates}
                      updateTableMetric={updateTableMetric}
                      PageType={pageTypeMap[props.PageType]}
                    />
                  );
                case "device":
                  return (
                    <DeviceElement
                      key={uniqueKey}
                      metric={item.metric}
                      arrayIndex={groupIndex}
                      removeArrayIndex={removeArrayIndex}
                      previousDates={props.previousDates}
                      updateTableMetric={updateTableMetric}
                      PageType={pageTypeMap[props.PageType]}
                    />
                  );
                case "auction_insights":
                  return (
                    <div
                      key={uniqueKey}
                      className="print:block print:break-inside-avoid print:page-break-inside-avoid w-full print:mt-4 mb-6"
                    >
                      <AuctionInsightsUploadChart
                        arrayIndex={groupIndex}
                        removeArrayIndex={removeArrayIndex}
                      />
                    </div>
                  );
                default:
                  return null;
              }
            }),
          )
        )}
      </div>
    </div>
  );
};

export default AddChart;

const AudienceElement = ({
  arrayIndex,
  metric,
  updateTableMetric,
  removeArrayIndex,
  previousDates,
  PageType,
}) => (
  <div className="mb-6 print:block print:break-inside-avoid print:page-break-inside-avoid print:mt-4">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-lg font-semibold text-blue-600">Audience</h2>
      <button
        className="p-1 text-md bg-gray-400 hover:bg-gray-500 text-white rounded-sm print:hidden"
        onClick={() => removeArrayIndex(arrayIndex)}
      >
        <IoMdClose />
      </button>
    </div>
    <Agechart
      previousDates={previousDates}
      metric={metric}
      updateTableMetric={updateTableMetric}
      arrayIndex={arrayIndex}
      PageType={PageType}
    />
  </div>
);

const GenderElement = ({
  arrayIndex,
  metric,
  updateTableMetric,
  removeArrayIndex,
  previousDates,
  PageType,
}) => (
  <div className="mb-6 print:block print:break-inside-avoid print:page-break-inside-avoid print:mt-4">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-lg font-semibold text-blue-600">Gender</h2>
      <button
        className="p-1 text-md bg-gray-400 hover:bg-gray-500 text-white rounded-sm print:hidden"
        onClick={() => removeArrayIndex(arrayIndex)}
      >
        <IoMdClose />
      </button>
    </div>
    <GenderChat
      previousDates={previousDates}
      metric={metric}
      updateTableMetric={updateTableMetric}
      arrayIndex={arrayIndex}
      PageType={PageType}
    />
  </div>
);

const DeviceElement = ({
  arrayIndex,
  metric,
  updateTableMetric,
  removeArrayIndex,
  previousDates,
  PageType,
}) => (
  <div className="mb-6 print:block print:break-inside-avoid print:page-break-inside-avoid print:mt-4">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-lg font-semibold text-blue-600">Device</h2>
      <button
        className="p-1 text-md bg-gray-400 hover:bg-gray-500 text-white rounded-sm print:hidden"
        onClick={() => removeArrayIndex(arrayIndex)}
      >
        <IoMdClose />
      </button>
    </div>
    <DeviceChart
      previousDates={previousDates}
      metric={metric}
      updateTableMetric={updateTableMetric}
      arrayIndex={arrayIndex}
      PageType={PageType}
    />
  </div>
);
